import React from "react";
import { EwaybillAccount, EwaybillAccountMappingResult, EwaybillShippingAddress } from "@/services/api/ewaybillAccount";
import { ColumnsType } from "antd/lib/table";
import EwaybillTypeNameMap from "@/map/EwaybillTypeNameMap";
import LogisticsCompanyNameMap from "@/map/LogisticsCompanyNameMap";
import { Button, message } from "antd";
import { useModalStore } from "@/hooks/Modal/hooks";
import ShareEwaybillModal from "../ShareEwaybillModal";
import moment from "moment";
import classNames from "classnames";
import EwaybillType from "@/enum/EwaybillType";
import {
  getJDLAuthUrl,
  getJDWJAuthUrl,
  getJDWLAuthUrl,
  getJRTTAuthUrlV2,
  getKWaiAuthUrl,
  getPddAuthUrlV2,
  getPddShopAuthUrlV2,
  getTaobaoAuthUrl,
  getXHSAuthUrl,
  isJDWLLogisticsCompany,
} from "@/services/api/ewaybillAuth";
import { useRequest } from "ahooks";
import UpdateEwaybillAccountNameButton from "./UpdateEwaybillAccountNameButton";
import DeleteEwaybillAccountButton from "./DeleteEwaybillAccountButton";

enum RowSpanType {
  ewaybillAccount,
  subscription,
  branchAccount,
  shippingAddress,
}

function getAutoRowSpan(
  result: Pick<
    EwaybillAccountMappingResult,
    | "allShippingAddresses"
    | "branchAccountMapping"
    | "ewaybillAccountMapping"
    | "ewaybillShippingAddressMapping"
    | "subscriptionMapping"
  >,
  currentData: EwaybillShippingAddress,
  currentIndex: number,
  targetProperties: RowSpanType[]
): React.TdHTMLAttributes<any> {
  const {
    allShippingAddresses: dataSource,
    ewaybillAccountMapping,
    subscriptionMapping,
    branchAccountMapping,
  } = result;

  // 如果是第一行默认不处理
  const prevIndex = currentIndex - 1;

  if (prevIndex > -1) {
    const ewaybillAccount = ewaybillAccountMapping.get(currentData.id);
    const subscription = subscriptionMapping.get(currentData.id);
    const branchAccount = branchAccountMapping.get(currentData.id);
    const prevData = dataSource[prevIndex];
    const prevDataEwaybillAccount = ewaybillAccountMapping.get(prevData.id);
    const prevDataSubscription = subscriptionMapping.get(prevData.id);
    const prevDataBranchAccount = branchAccountMapping.get(prevData.id);

    const hasSameFlag = targetProperties.every((targetProperty) => {
      if (targetProperty === RowSpanType.ewaybillAccount) {
        return ewaybillAccount === prevDataEwaybillAccount;
      }

      if (targetProperty === RowSpanType.subscription) {
        return subscription === prevDataSubscription;
      }

      if (targetProperty === RowSpanType.branchAccount) {
        return branchAccount === prevDataBranchAccount;
      }

      return false;
    });

    if (hasSameFlag) {
      return {
        rowSpan: 0,
      };
    }
  }

  let rowSpan = 1;

  for (let i = currentIndex; i < dataSource.length; i++) {
    const currentLocalConfig = dataSource[i];
    const nextLocalConfig = dataSource[i + 1];

    const ewaybillAccount = ewaybillAccountMapping.get(currentLocalConfig.id);
    const subscription = subscriptionMapping.get(currentLocalConfig.id);
    const branchAccount = branchAccountMapping.get(currentLocalConfig.id);
    const nextEwaybillAccount = ewaybillAccountMapping.get(nextLocalConfig?.id);
    const nextSubscription = subscriptionMapping.get(nextLocalConfig?.id);
    const nextBranchAccount = branchAccountMapping.get(nextLocalConfig?.id);

    const hasSameFlag = targetProperties.every((targetProperty) => {
      if (targetProperty === RowSpanType.ewaybillAccount) {
        return ewaybillAccount === nextEwaybillAccount;
      }

      if (targetProperty === RowSpanType.subscription) {
        return subscription === nextSubscription;
      }

      if (targetProperty === RowSpanType.branchAccount) {
        return branchAccount === nextBranchAccount;
      }
    });

    if (hasSameFlag) {
      rowSpan++;
    } else {
      break;
    }
  }

  return {
    rowSpan,
  };
}

interface ExpiredButtonProps {
  ewaybillAccount: EwaybillAccount;
}

const ExpiredButton: React.FC<ExpiredButtonProps> = function (props) {
  const { ewaybillAccount } = props;

  const { type: ewaybillType, subscriptions, platformUsername } = ewaybillAccount;

  const { run, loading } = useRequest(
    async (e) => {
      e.preventDefault();

      switch (ewaybillType) {
        case EwaybillType.PDD: {
          const { url } = await getPddShopAuthUrlV2();

          window.location.href = url;
          break;
        }
        case EwaybillType.PDD_AUTH: {
          const { url } = await getPddAuthUrlV2();

          window.location.href = url;

          break;
        }
        case EwaybillType.JINRITEMAI: {
          const { url } = await getJRTTAuthUrlV2();

          window.location.href = url;

          break;
        }
        case EwaybillType.JINRITEMAI_AUTH: {
          const { url } = await getJRTTAuthUrlV2("/ewaybillAccount", true);

          window.location.href = url;

          break;
        }
        case EwaybillType.TAOBAO_CAINIAO_AUTH: {
          const { url } = await getTaobaoAuthUrl();

          window.location.href = url;
          break;
        }
        case EwaybillType.JD: {
          const { url } = await getJDWJAuthUrl();

          window.location.href = url;

          break;
        }
        case EwaybillType.JDL: {
          const { url } = await getJDLAuthUrl(platformUsername);

          window.location.href = url;
          break;
        }

        case EwaybillType.OFFICIAL: {
          const logisticsCompany = subscriptions?.[0]?.logisticsCompany;

          if (isJDWLLogisticsCompany(logisticsCompany)) {
            const { url } = await getJDWLAuthUrl(platformUsername, logisticsCompany);

            window.location.href = url;
            return;
          }

          message.error("当前电子面单类型尚未实现");
          break;
        }
        case EwaybillType.KWAISHOP: {
          const { url } = await getKWaiAuthUrl();

          window.location.href = url;

          break;
        }
        case EwaybillType.XIAOHONGSHU: {
          const { url } = await getXHSAuthUrl();

          window.location.href = url;

          break;
        }
        default:
          message.error("当前电子面单类型尚未实现");
          break;
      }
    },
    {
      manual: true,
    }
  );

  return (
    <Button loading={loading} size="small" type="link" onClick={run}>
      重新授权
    </Button>
  );
};

interface UseColumnsProps extends EwaybillAccountMappingResult {
  onRefresh: () => void;
}

export default function useColumns(props: UseColumnsProps): ColumnsType<EwaybillShippingAddress> {
  const {
    subscriptionMapping,
    branchAccountMapping,
    ewaybillAccountMapping,
    ewaybillShippingAddressMapping,
    ewaybillAccountExpiredResultMapping,
    allShippingAddresses,
    onRefresh,
  } = props;

  const modalStore = useModalStore();

  return [
    {
      title: "电子面单账号",
      key: "ewaybillAccountInfo",
      onCell(shippingAddress, index) {
        const result = getAutoRowSpan(
          {
            subscriptionMapping,
            branchAccountMapping,
            ewaybillAccountMapping,
            ewaybillShippingAddressMapping,
            allShippingAddresses,
          },
          shippingAddress,
          index,
          [RowSpanType.ewaybillAccount]
        );

        return result;
      },
      render: (_, shippingAddress, index) => {
        const ewaybillAccount = ewaybillAccountMapping.get(shippingAddress.id);

        if (!ewaybillAccount) return null;

        const expiredResult = ewaybillAccountExpiredResultMapping.get(ewaybillAccount.id);
        const showOriginalName =
          ewaybillAccount.type === EwaybillType.PDD_AUTH && ewaybillAccount.name !== ewaybillAccount.platformUsername;

        const isDeleted = ewaybillAccount.isDeleted;

        return (
          <div>
            <div className="text-small">
              <div>
                <span>{ewaybillAccount.name ?? ewaybillAccount.platformUsername}</span>
                {showOriginalName ? (
                  <span className="ml5" style={{ color: "#777", fontSize: "85%" }}>
                    ({ewaybillAccount.platformUsername})
                  </span>
                ) : null}

                {!isDeleted ? (
                  <DeleteEwaybillAccountButton ewaybillAccount={ewaybillAccount} onRefresh={onRefresh} />
                ) : (
                  <span className="ml5" style={{ color: "#777", fontSize: "85%" }}>
                    （已删除）
                  </span>
                )}

                {ewaybillAccount.type === EwaybillType.PDD_AUTH && !isDeleted ? (
                  <UpdateEwaybillAccountNameButton ewaybillAccount={ewaybillAccount} onRefresh={onRefresh} />
                ) : null}
              </div>
              <div>{EwaybillTypeNameMap[ewaybillAccount.type]}</div>

              <div
                className={classNames({
                  "text-error": expiredResult?.hasExpired || expiredResult?.willExpired,
                })}
              >
                {expiredResult?.tokenExpiringTimeMillis > 0
                  ? `授权到期: ${moment(expiredResult.tokenExpiringTimeMillis).format("YYYY-MM-DD")}`
                  : null}
              </div>
            </div>

            {expiredResult.hasExpired || expiredResult.willExpired ? (
              <div className="text-error">
                {expiredResult.hasExpired ? (
                  <span>已过期</span>
                ) : expiredResult.willExpired ? (
                  <span>即将过期</span>
                ) : null}

                <ExpiredButton ewaybillAccount={ewaybillAccount}></ExpiredButton>
              </div>
            ) : null}
          </div>
        );
      },
    },
    {
      title: "快递公司",
      key: "logisticsCompany",
      onCell(shippingAddress, index) {
        const result = getAutoRowSpan(
          {
            subscriptionMapping,
            branchAccountMapping,
            ewaybillAccountMapping,
            ewaybillShippingAddressMapping,
            allShippingAddresses,
          },
          shippingAddress,
          index,
          [RowSpanType.ewaybillAccount, RowSpanType.subscription]
        );

        return result;
      },
      render: (_, shippingAddress, index) => {
        const subscription = subscriptionMapping.get(shippingAddress.id);

        const logisticsCompany = LogisticsCompanyNameMap[subscription.logisticsCompany];

        return <span>{logisticsCompany}</span>;
      },
    },
    {
      title: "网点名称（编码）",
      dataIndex: "branchInfo",
      onCell(shippingAddress, index) {
        const result = getAutoRowSpan(
          {
            subscriptionMapping,
            branchAccountMapping,
            ewaybillAccountMapping,
            ewaybillShippingAddressMapping,
            allShippingAddresses,
          },
          shippingAddress,
          index,
          [RowSpanType.ewaybillAccount, RowSpanType.subscription, RowSpanType.branchAccount]
        );

        return result;
      },
      render: (_, shippingAddress, index) => {
        const branchAccount = branchAccountMapping.get(shippingAddress.id);

        const nameStrs: string[] = [];

        let name = `${branchAccount.branchName ? branchAccount.branchName : ""}${
          branchAccount.branchCode ? `(${branchAccount.branchCode})` : ""
        }`;
        if (name) {
          nameStrs.push(name);
        }

        if (branchAccount.waybillNoTypeName) {
          nameStrs.push(branchAccount.waybillNoTypeName);
        }

        return <span>{nameStrs.join("-")}</span>;
      },
    },
    {
      title: "账户信息",
      key: "accountQuantityInfo",
      onCell(shippingAddress, index) {
        const result = getAutoRowSpan(
          {
            subscriptionMapping,
            branchAccountMapping,
            ewaybillAccountMapping,
            ewaybillShippingAddressMapping,
            allShippingAddresses,
          },
          shippingAddress,
          index,
          [RowSpanType.ewaybillAccount, RowSpanType.subscription, RowSpanType.branchAccount]
        );

        return result;
      },
      render: (_, shippingAddress, index) => {
        const branchAccount = branchAccountMapping.get(shippingAddress.id);

        const { quantity, allocatedQuantity, cancelQuantity } = branchAccount || {};

        return (
          <div className={"noWrap"}>
            <div>余额：{quantity}</div>

            <div>已使用：{allocatedQuantity}</div>

            <div>已取消：{cancelQuantity}</div>
          </div>
        );
      },
    },
    {
      title: "发货地址",
      key: "shippingAddresses",
      render: (_, shippingAddress, index) => {
        const { province, city, district, town, street } = shippingAddress;

        const joinedShippingAddress = [province, city, district, town, street].filter((it) => it).join("");

        return <div>{joinedShippingAddress}</div>;
      },
    },

    {
      title: "操作",
      key: "operation",
      render: (_, shippingAddress, index) => {
        const subscription = subscriptionMapping.get(shippingAddress.id);
        const ewaybillBranchAccount = branchAccountMapping.get(shippingAddress.id);
        const ewaybillAccount = ewaybillAccountMapping.get(shippingAddress.id);

        if (ewaybillBranchAccount.needIgnoreWhenGetShareCode) return null;
        if (ewaybillAccount.isDeleted) return null;

        return (
          <Button
            type="primary"
            onClick={() => {
              modalStore.openModal(ShareEwaybillModal, {
                logisticsCompany: subscription.logisticsCompany,
                ewaybillBranchAccount,
                shippingAddress,
              });
            }}
          >
            共享
          </Button>
        );
      },
    },
  ];
}
